/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Plus, Trash2, Download, Archive, Search, X, Check,
  LayoutGrid, List, Film, Users, Shirt, Calendar,
  Package, ClipboardList, BarChart2, GripVertical,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ImageUploadButton } from '../components/ImageUpload';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): ImageThumbStrip
// + LightboxThumb were wired for costume gallery strips; callsites dropped.
import { ImageThumbStrip, LightboxThumb } from '../components/ImageUpload';
import { AuthImage } from '../components/AuthImage';
import LookAndFeelGallery from '../components/LookAndFeelGallery';
import { toast } from 'sonner';
import { formatSceneNumber } from '../utils/sceneNumber';
import ArtAIGenerateModal from '../components/ArtAIGenerateModal';
import PDFPreviewDialog from '../components/PDFPreviewDialog';
import DepartmentTasksPanel from '../components/DepartmentTasksPanel';
import { LanguageDownloadDialog } from '../components/LanguageDownloadDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPT = 'COSTUME_WARDROBE';

const COSTUME_CATEGORIES = [
  { value: 'hero_costume', label: 'Hero Costume' },
  { value: 'day_wear', label: 'Day Wear' },
  { value: 'evening_wear', label: 'Evening Wear' },
  { value: 'period_costume', label: 'Period Costume' },
  { value: 'uniform', label: 'Uniform' },
  { value: 'accessory', label: 'Accessory' },
  { value: 'other', label: 'Other' },
];
const SOURCES = ['purchase', 'rent', 'make', 'borrow', 'other'];
const BLANK_COSTUME = { description: '', asset_sub_type: 'day_wear', character_name: '', source: 'purchase', notes: '', sceneIds: [] };

const COSTUME_STATUS = {
  locked:      { label: 'Locked',      color: '#4ade80', bg: 'rgba(74,222,128,.1)',  border: 'rgba(74,222,128,.25)' },
  'in-review': { label: 'In Review',   color: '#fbbf24', bg: 'rgba(251,191,36,.1)',  border: 'rgba(251,191,36,.25)' },
  pending:     { label: 'Pending',     color: '#f87171', bg: 'rgba(248,113,113,.1)', border: 'rgba(248,113,113,.25)' },
  'to-fit':    { label: 'To Fit',      color: '#60a5fa', bg: 'rgba(96,165,250,.1)',  border: 'rgba(96,165,250,.25)' },
  unreviewed:  { label: 'Unreviewed',  color: '#6b7280', bg: 'rgba(107,114,128,.08)', border: 'rgba(107,114,128,.2)' },
};
const STATUS_CYCLE = ['unreviewed', 'to-fit', 'in-review', 'locked'];

const AVATAR_COLORS = ['#6366f1','#ec4899','#f97316','#14b8a6','#a855f7','#0ea5e9','#84cc16','#f59e0b','#ef4444','#10b981'];
const avatarColor = (name) => AVATAR_COLORS[(name||'').split('').reduce((a,c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const avatarInitials = (name) => (name||'?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();

// ── Circular progress ────────────────────────────────────────────────────────
function CircleProgress({ pct = 0, size = 72 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--cb-border)" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#4ade80" strokeWidth="6"
          strokeDasharray={`${circ * Math.min(pct,100) / 100} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#4ade80', lineHeight: 1 }}>{pct}%</span>
        <span style={{ fontSize: 7, color: 'var(--cb-text-dim)', textAlign: 'center', lineHeight: 1.2, marginTop: 1 }}>Looks<br/>Ready</span>
      </div>
    </div>
  );
}

// ── Character avatar ──────────────────────────────────────────────────────────
function Avatar({ name, size = 28, showName = false, subLabel = '' }) {
  const bg = avatarColor(name);
  const ini = avatarInitials(name);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ width: size, height: size, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: 'var(--cb-text)', border: '2px solid var(--cb-bg)', flexShrink: 0 }}>
        {ini}
      </div>
      {showName && <div style={{ fontSize: 9, color: 'var(--cb-text-sec)', textAlign: 'center', whiteSpace: 'nowrap', maxWidth: 52, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>}
      {subLabel && <div style={{ fontSize: 8, color: 'var(--cb-text-dim)', textAlign: 'center' }}>{subLabel}</div>}
    </div>
  );
}

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, onClick }) {
  const cfg = COSTUME_STATUS[status] || COSTUME_STATUS.unreviewed;
  return (
    <button onClick={onClick} style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </button>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────
function CostumeSidebar({ section, setSection, onAddCostume, onAddCharacter, onAISuggest, onRecycleBin, user }) {
  const navItems = [
    { key: 'scenes',     label: 'Scenes',         icon: Film },
    { key: 'characters', label: 'Characters',      icon: Users },
    { key: 'looks',      label: 'Looks',           icon: Shirt },
    { key: 'fittings',   label: 'Fittings',        icon: Calendar },
    { key: 'itembox',    label: 'Item Box',         icon: Package },
    { key: 'tasks',      label: 'Tasks',            icon: ClipboardList },
    { key: 'reports',    label: 'Reports',          icon: BarChart2 },
  ];
  return (
    <div style={{ width: 192, background: '#0a0c10', borderRight: '1px solid var(--cb-border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      <div style={{ padding: '12px 12px 0' }}>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-border)', paddingLeft: 2, paddingBottom: 6 }}>Costume Department</p>
        {navItems.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setSection(key)} data-testid={`costume-nav-${key}`}
            className="w-full text-left flex items-center gap-2 rounded px-2 py-1.5 mb-0.5 transition-colors text-xs"
            style={{ color: section === key ? 'var(--cb-accent)' : '#777', background: section === key ? 'rgba(245,166,35,.07)' : 'transparent', borderLeft: section === key ? '2px solid var(--cb-accent)' : '2px solid transparent', fontWeight: section === key ? 600 : 400 }}>
            <Icon size={12} style={{ opacity: section === key ? 1 : .5, flexShrink: 0 }} />
            {label}
          </button>
        ))}
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--cb-border)', marginTop: 8 }}>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-border)', paddingLeft: 2, paddingBottom: 4 }}>Quick Actions</p>
        {[
          { label: '+ Add Costume Item', fn: onAddCostume, testid: 'qa-add-costume' },
          { label: '+ Add Character',    fn: onAddCharacter, testid: 'costume-add-character-btn' },
          { label: '+ Add Fitting',      fn: () => toast.info('Fittings coming soon'), testid: 'qa-add-fitting' },
          { label: '✦ AI Suggest Costume', fn: onAISuggest, testid: 'costume-ai-generate-sidebar-btn' },
        ].map(({ label, fn, testid }) => (
          <button key={testid} onClick={fn} data-testid={testid}
            className="w-full text-left px-2 py-1 text-xs rounded transition-colors mb-0.5 hover:text-amber-400"
            style={{ color: 'var(--cb-text-dim)' }}>{label}</button>
        ))}
        <button onClick={onRecycleBin} data-testid="recycle-bin-btn"
          className="w-full text-left px-2 py-1 text-xs flex items-center gap-1.5 mt-1 hover:text-red-400 transition-colors"
          style={{ color: 'var(--cb-text-dim)' }}>
          <Archive size={10} /> Recycle Bin
        </button>
      </div>
      <div style={{ marginTop: 'auto', padding: '10px 12px', borderTop: '1px solid var(--cb-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#2a1a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#a78bfa' }}>
          {avatarInitials(user?.full_name || user?.name || 'U')}
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--cb-text-sec)', fontWeight: 600 }}>{(user?.full_name || user?.name || 'User').split(' ')[0]}</div>
          <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>Costume Department</div>
        </div>
      </div>
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────────────
function CostumeHeader({ scenes, section, setSection, onAddCostume, onExport, onExportPdf }) {
  const totalChars = [...new Set(scenes.flatMap(s => s.characters_in_scene || []))].length;
  const totalLooks = scenes.reduce((a, s) => a + (s.breakdown_items?.COSTUME || []).length, 0);
  const looksReady = scenes.reduce((a, s) => a + (s.breakdown_items?.COSTUME || []).filter(i => (i.image_paths||[]).length > 0).length, 0);
  const subTabs = [
    { key: 'scenes', label: 'Scenes' },
    { key: 'characters', label: 'Characters' },
    { key: 'looks', label: 'Looks' },
    { key: 'fittings', label: 'Fittings' },
    { key: 'itembox', label: 'Item Box' },
  ];
  return (
    <div style={{ borderBottom: '1px solid var(--cb-border)', padding: '12px 20px 0', flexShrink: 0 }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ color: 'var(--cb-text)' }}>Costume Department</h1>
          <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginTop: 2 }}>Manage costumes, looks, fittings and wardrobe for every scene.</p>
        </div>
        <div className="flex items-center gap-4">
          {[
            { num: scenes.length, label: 'Scenes' },
            { num: totalChars,    label: 'Characters' },
            { num: totalLooks,    label: 'Looks' },
            { num: 0,             label: 'Fittings' },
            { num: looksReady,    label: 'Looks Ready' },
          ].map(({ num, label }) => (
            <div key={label} className="text-center">
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cb-accent)', lineHeight: 1 }}>{num}</div>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--cb-text-dim)', marginTop: 1 }}>{label}</div>
            </div>
          ))}
          <div className="flex gap-2 ml-2">
            <Button size="sm" variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8 text-xs" data-testid="export-list-btn" onClick={onExport}>
              <Download className="h-3.5 w-3.5 mr-1" /> Export List
            </Button>
            <Button size="sm" variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8 text-xs" data-testid="costume-export-pdf-btn" onClick={onExportPdf} title="Preview & download Costume breakdown PDF">
              <Download className="h-3.5 w-3.5 mr-1" /> Export PDF
            </Button>
            <Button size="sm" className="h-8 text-xs font-bold text-black" style={{ background: 'var(--cb-accent)' }} onClick={onAddCostume} data-testid="header-add-costume-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Costume Item
            </Button>
          </div>
        </div>
      </div>
      <div className="flex">
        {subTabs.map(({ key, label }) => (
          <button key={key} onClick={() => setSection(key)}
            style={{ padding: '8px 16px', fontSize: 12, color: section === key ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderBottom: section === key ? '2px solid var(--cb-accent)' : '2px solid transparent', background: 'none', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Scene card ────────────────────────────────────────────────────────────────
function CostumeSceneCard({ scene, status, onSelect, onCycleStatus, onAddCostume, isSelected, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const costumes = scene.breakdown_items?.COSTUME || [];
  const extras   = scene.breakdown_items?.EXTRA_BACKGROUND || [];
  const chars    = scene.characters_in_scene || [];
  const looksCount = [...new Set(costumes.map(c => c.character_name).filter(Boolean))].length || costumes.length;
  const synopsis = (scene.synopsis_en || scene.synopsis || '').slice(0, 100);

  return (
    <div
      draggable onDragStart={onDragStart} onDragOver={e => { e.preventDefault(); onDragOver(); }} onDrop={e => { e.preventDefault(); onDrop(); }} onDragEnd={onDragEnd}
      onClick={() => onSelect(scene)}
      data-testid={`costume-scene-${scene.scene_number ?? 'unnumbered'}`}
      style={{ background: 'var(--cb-surface)', border: isSelected ? '1px solid var(--cb-accent)' : isDragOver ? '1px dashed var(--cb-accent)' : '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', opacity: isDragOver ? .7 : 1, transition: 'border-color .15s' }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(245,166,35,.4)'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--cb-border)'; }}
    >
      {/* Drag handle */}
      <div style={{ padding: '3px 12px 0', display: 'flex', justifyContent: 'center', opacity: .2 }} onClick={e => e.stopPropagation()}>
        <GripVertical size={12} style={{ color: 'var(--cb-text-sec)' }} />
      </div>
      <div style={{ padding: '8px 12px 10px' }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--cb-accent)', background: 'rgba(245,166,35,.12)', padding: '2px 7px', borderRadius: 4 }}>SC.{formatSceneNumber(scene.scene_number)}</span>
            <span style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{(scene.location_type||'INT').toUpperCase()} · {(scene.time_of_day||'DAY').toUpperCase()}</span>
          </div>
          <StatusBadge status={status} onClick={e => { e.stopPropagation(); onCycleStatus(scene.id); }} />
        </div>
        {/* Title */}
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cb-text)', lineHeight: 1.3, marginBottom: 4 }}>{scene.scene_header}</div>
        {/* Synopsis */}
        {synopsis && <div style={{ fontSize: 10, color: '#777', lineHeight: 1.5, marginBottom: 10, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{synopsis}</div>}
        {/* Character avatars */}
        {chars.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: -4, marginBottom: 10, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {chars.slice(0, 5).map((c, i) => (
              <div key={c} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 5 - i }}>
                <Avatar name={c} size={28} />
              </div>
            ))}
            {chars.length > 5 && (
              <div style={{ marginLeft: -8, width: 28, height: 28, borderRadius: '50%', background: 'var(--cb-border)', border: '2px solid var(--cb-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--cb-text-sec)', zIndex: 0 }}>
                +{chars.length - 5}
              </div>
            )}
          </div>
        )}
        {/* Stats */}
        <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--cb-border)', paddingTop: 8, marginTop: chars.length ? 0 : 8 }}>
          {[['Characters', chars.length], ['Looks', looksCount], ['Extras', extras.length]].map(([label, count], i) => (
            <div key={label} style={{ flex: 1, textAlign: 'center', borderRight: i < 2 ? '1px solid var(--cb-border)' : 'none' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cb-text)' }}>{count}</div>
              <div style={{ fontSize: 9, color: 'var(--cb-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Right Panel ───────────────────────────────────────────────────────────────
function CostumeRightPanel({ scene, status, onClose, onCycleStatus, onDelete, fetchAll, token, allScenes, sceneIndex, onNavigate, onAddCostume }) {
  const [tab, setTab] = useState('overview');
  const [extrasNote, setExtrasNote] = useState(() => { try { return localStorage.getItem(`costume_extras_note_${scene.id}`) || ''; } catch { return ''; } });
  const [tasks, setTasks] = useState(() => { try { return JSON.parse(localStorage.getItem(`costume_tasks_${scene.id}`) || '[]'); } catch { return []; } });
  const [newTask, setNewTask] = useState('');

  const costumes = scene.breakdown_items?.COSTUME || [];
  const extras   = scene.breakdown_items?.EXTRA_BACKGROUND || [];
  const chars    = scene.characters_in_scene || [];
  const byChar   = costumes.reduce((acc, item) => { const c = item.character_name || 'Unassigned'; (acc[c] = acc[c] || []).push(item); return acc; }, {});
  const looksCount = [...new Set(costumes.map(c => c.character_name).filter(Boolean))].length || costumes.length;
  const readyCount = costumes.filter(i => (i.image_paths||[]).length > 0).length;
  const readyPct  = costumes.length ? Math.round(readyCount / costumes.length * 100) : 0;
  const synopsis  = scene.synopsis_en || scene.synopsis || '';

  const saveExtrasNote = (val) => { setExtrasNote(val); try { localStorage.setItem(`costume_extras_note_${scene.id}`, val); } catch {} };
  const saveTasks = (t) => { setTasks(t); try { localStorage.setItem(`costume_tasks_${scene.id}`, JSON.stringify(t)); } catch {} };
  const addTask = () => { if (!newTask.trim()) return; saveTasks([...tasks, { id: Date.now(), text: newTask.trim(), status: 'pending' }]); setNewTask(''); };
  const cycleTask = (id) => {
    const order = ['pending', 'in-progress', 'done'];
    saveTasks(tasks.map(t => t.id === id ? { ...t, status: order[(order.indexOf(t.status) + 1) % order.length] } : t));
  };
  const removeTask = (id) => saveTasks(tasks.filter(t => t.id !== id));

  const TASK_STYLE = { done: { color: '#4ade80', label: 'Done' }, 'in-progress': { color: '#fbbf24', label: 'In Progress' }, pending: { color: '#6b7280', label: 'Pending' } };

  const rpTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'characters', label: `Characters (${chars.length})` },
    { key: 'looks', label: `Looks (${looksCount})` },
    { key: 'items', label: `Costume Items (${costumes.length})` },
    { key: 'extras', label: `Extras (${extras.length})` },
    { key: 'notes', label: 'Notes' },
  ];

  return (
    <div style={{ width: 440, borderLeft: '1px solid var(--cb-border)', background: 'var(--cb-bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--cb-accent)' }}>SC.{formatSceneNumber(scene.scene_number)}</span>
            <span style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>{(scene.location_type||'INT').toUpperCase()} · {(scene.time_of_day||'DAY').toUpperCase()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusBadge status={status} onClick={() => onCycleStatus(scene.id)} />
            <button onClick={() => onNavigate(-1)} disabled={sceneIndex <= 0} style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--cb-border)', border: 'none', color: sceneIndex <= 0 ? 'var(--cb-border)' : 'var(--cb-text-sec)', cursor: sceneIndex <= 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={12} /></button>
            <button onClick={() => onNavigate(1)} disabled={sceneIndex >= allScenes.length - 1} style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--cb-border)', border: 'none', color: sceneIndex >= allScenes.length - 1 ? 'var(--cb-border)' : 'var(--cb-text-sec)', cursor: sceneIndex >= allScenes.length - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={12} /></button>
            <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--cb-border)', border: 'none', color: 'var(--cb-text-sec)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>✕</button>
          </div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--cb-text)', lineHeight: 1.2, marginBottom: 2 }}>{scene.scene_header}</div>
        <div style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>Day {formatSceneNumber(scene.scene_number)} &nbsp;·&nbsp; {scene.location_name || 'Unknown Set'}</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--cb-border)', overflowX: 'auto', flexShrink: 0 }}>
        {rpTabs.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '7px 10px', fontSize: 10, color: tab === key ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderBottom: tab === key ? '2px solid var(--cb-accent)' : '2px solid transparent', whiteSpace: 'nowrap', background: 'none', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>

        {/* ── Overview ── */}
        {tab === 'overview' && (
          <>
            {/* Scene Summary */}
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700, marginBottom: 10 }}>Scene Summary</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[['👤', chars.length, 'Characters'], ['👔', looksCount, 'Looks'], ['📋', costumes.length, 'Costume Items'], ['👥', extras.length, 'Extras']].map(([icon, val, label]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11 }}>{icon}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cb-text)', lineHeight: 1 }}>{val}</div>
                    <div style={{ fontSize: 8, color: 'var(--cb-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
                  </div>
                ))}
              </div>
              <CircleProgress pct={readyPct} size={72} />
            </div>

            {/* Characters in scene */}
            {chars.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Characters in this Scene</p>
                  <button onClick={() => setTab('characters')} style={{ fontSize: 9, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {chars.map(c => {
                    const charLooks = (byChar[c] || []).length;
                    return <Avatar key={c} name={c} size={36} showName subLabel={`Look ${charLooks}`} />;
                  })}
                </div>
              </div>
            )}

            {/* Looks in scene */}
            {costumes.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Looks in this Scene</p>
                  <button onClick={() => setTab('looks')} style={{ fontSize: 9, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
                </div>
                <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
                  {costumes.slice(0, 6).map((item, i) => (
                    <div key={item.id} style={{ width: 52, height: 64, borderRadius: 6, overflow: 'hidden', background: 'var(--cb-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.image_paths?.length > 0
                        ? <AuthImage path={item.image_paths[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 18 }}>👔</span>}
                    </div>
                  ))}
                  {costumes.length > 6 && <div style={{ width: 52, height: 64, borderRadius: 6, background: 'var(--cb-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--cb-text-dim)' }}>+{costumes.length - 6}</div>}
                </div>
              </div>
            )}

            {/* Extras */}
            {(extras.length > 0 || extrasNote) && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700, marginBottom: 8 }}>Extras in this Scene ({extras.length})</p>
                {extras.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {extras.slice(0, 6).map((e, i) => (
                      <Avatar key={e.id || i} name={e.description || `Extra ${i+1}`} size={28} />
                    ))}
                    {extras.length > 6 && <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--cb-text-dim)' }}>+{extras.length-6}</div>}
                  </div>
                )}
                {extrasNote && <p style={{ fontSize: 10, color: 'var(--cb-text-sec)', fontStyle: 'italic', lineHeight: 1.5 }}>{extrasNote}</p>}
                {!extrasNote && (
                  <textarea placeholder="Add extras note..." value={extrasNote} onChange={e => saveExtrasNote(e.target.value)}
                    style={{ width: '100%', background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 4, padding: '6px 8px', fontSize: 10, color: 'var(--cb-text-sec)', resize: 'none', outline: 'none', minHeight: 40 }} />
                )}
              </div>
            )}

            {/* Tasks */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Tasks ({tasks.length})</p>
                <button onClick={() => setTab('notes')} style={{ fontSize: 9, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
              </div>
              {tasks.slice(0, 4).map(t => {
                const st = TASK_STYLE[t.status] || TASK_STYLE.pending;
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #1a1c24' }}>
                    <button onClick={() => cycleTask(t.id)} style={{ width: 14, height: 14, borderRadius: 3, border: `1px solid ${st.color}`, background: t.status === 'done' ? st.color : 'transparent', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {t.status === 'done' && <Check size={9} style={{ color: 'var(--cb-bg)' }} />}
                    </button>
                    <span style={{ flex: 1, fontSize: 11, color: t.status === 'done' ? 'var(--cb-text-dim)' : 'var(--cb-text-sec)', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.text}</span>
                    <span style={{ fontSize: 9, color: st.color, fontWeight: 700, background: `${st.color}15`, padding: '1px 6px', borderRadius: 3 }}>{st.label}</span>
                    <button onClick={() => removeTask(t.id)} style={{ background: 'none', border: 'none', color: 'var(--cb-border)', cursor: 'pointer', padding: 0 }}><X size={10} /></button>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add a task…"
                  onKeyDown={e => e.key === 'Enter' && addTask()}
                  style={{ flex: 1, background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11, color: 'var(--cb-text-sec)', outline: 'none' }} />
                <button onClick={addTask} style={{ padding: '4px 8px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>+</button>
              </div>
            </div>

            {/* Quick Actions */}
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700, marginBottom: 8 }}>Quick Actions</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                { label: '+ Add Costume Item', amber: true, fn: () => onAddCostume(scene) },
                { label: '+ Add Character',    amber: true, fn: () => toast.info('Coming soon') },
                { label: '+ Add Fitting',      amber: false, fn: () => toast.info('Coming soon') },
                { label: '✦ AI Suggest Costume', amber: false, fn: () => toast.info('Coming soon') },
              ].map(({ label, amber, fn }, i) => (
                <button key={i} onClick={fn}
                  style={{ padding: '6px 8px', border: amber ? '1px solid rgba(245,166,35,.3)' : '1px solid var(--cb-border)', borderRadius: 6, fontSize: 10, color: amber ? 'var(--cb-accent)' : 'var(--cb-text-sec)', background: amber ? 'rgba(245,166,35,.08)' : 'var(--cb-surface)', cursor: 'pointer', textAlign: 'center' }}>
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Characters tab ── */}
        {tab === 'characters' && (
          <div>
            {chars.length === 0
              ? <p style={{ color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No characters assigned</p>
              : chars.map(c => {
                const items = byChar[c] || [];
                return (
                  <div key={c} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Avatar name={c} size={32} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cb-text)' }}>{c}</div>
                        <div style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>{items.length} looks</div>
                      </div>
                    </div>
                    {items.map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #1a1c24' }} data-testid={`costume-item-${item.id}`}>
                        <div style={{ width: 32, height: 40, borderRadius: 4, overflow: 'hidden', background: 'var(--cb-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {item.image_paths?.length > 0 ? <AuthImage path={item.image_paths[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 14 }}>👔</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: 'var(--cb-text)' }}>{item.description}</div>
                          <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{item.asset_sub_type || 'costume'}</div>
                        </div>
                        <ImageUploadButton itemId={item.id} onUploaded={fetchAll} />
                        <button onClick={() => onDelete(item.id)} style={{ background: 'none', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer' }}><Trash2 size={11} /></button>
                      </div>
                    ))}
                  </div>
                );
              })}
          </div>
        )}

        {/* ── Looks tab ── */}
        {tab === 'looks' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {costumes.length === 0 ? <p style={{ gridColumn: 'span 2', color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No looks recorded</p>
              : costumes.map(item => (
                <div key={item.id} style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden' }} data-testid={`costume-item-${item.id}`}>
                  <div style={{ height: 80, background: 'var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {item.image_paths?.length > 0 ? <AuthImage path={item.image_paths[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 24 }}>👔</span>}
                  </div>
                  <div style={{ padding: '6px 8px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--cb-text)', marginBottom: 1 }}>{item.description}</div>
                    {item.character_name && <div style={{ fontSize: 9, color: avatarColor(item.character_name), fontWeight: 600 }}>{item.character_name}</div>}
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <ImageUploadButton itemId={item.id} onUploaded={fetchAll} />
                      <button onClick={() => onDelete(item.id)} style={{ background: 'none', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer', padding: 1 }}><Trash2 size={10} /></button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ── Costume Items tab ── */}
        {tab === 'items' && (
          costumes.length === 0
            ? <p style={{ color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No costume items</p>
            : costumes.map(item => (
              <div key={item.id} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }} data-testid={`costume-item-${item.id}`}>
                <div style={{ width: 28, height: 36, borderRadius: 4, overflow: 'hidden', background: 'var(--cb-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.image_paths?.length > 0 ? <AuthImage path={item.image_paths[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span>👔</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--cb-text)', fontWeight: 600 }}>{item.description}</div>
                  <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{item.character_name || 'Unassigned'} · {item.asset_sub_type}</div>
                </div>
                <ImageUploadButton itemId={item.id} onUploaded={fetchAll} />
                <button onClick={() => onDelete(item.id)} style={{ background: 'none', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer' }}><Trash2 size={11} /></button>
              </div>
            ))
        )}

        {/* ── Extras tab ── */}
        {tab === 'extras' && (
          <div>
            <div style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-text-sec)', marginBottom: 8 }}>Extras Note</p>
              <textarea value={extrasNote} onChange={e => saveExtrasNote(e.target.value)} placeholder="Describe the extras required for this scene..."
                style={{ width: '100%', background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', borderRadius: 4, padding: '6px 8px', fontSize: 11, color: 'var(--cb-text-sec)', resize: 'vertical', outline: 'none', minHeight: 80 }} />
            </div>
            {extras.length === 0
              ? <p style={{ color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No extras recorded (EXTRA_BACKGROUND items)</p>
              : extras.map(item => (
                <div key={item.id} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                  <Avatar name={item.description || 'Extra'} size={28} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--cb-text)' }}>{item.description}</div>
                    <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{item.notes || 'Extra / Background'}</div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ── Notes tab ── */}
        {tab === 'notes' && (
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700, marginBottom: 10 }}>All Tasks ({tasks.length})</p>
            {tasks.map(t => {
              const st = TASK_STYLE[t.status] || TASK_STYLE.pending;
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                  <button onClick={() => cycleTask(t.id)} style={{ width: 14, height: 14, borderRadius: 3, border: `1px solid ${st.color}`, background: t.status === 'done' ? st.color : 'transparent', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {t.status === 'done' && <Check size={9} style={{ color: 'var(--cb-bg)' }} />}
                  </button>
                  <span style={{ flex: 1, fontSize: 11, color: t.status === 'done' ? 'var(--cb-text-dim)' : 'var(--cb-text-sec)', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.text}</span>
                  <span style={{ fontSize: 9, color: st.color, fontWeight: 700 }}>{st.label}</span>
                  <button onClick={() => removeTask(t.id)} style={{ background: 'none', border: 'none', color: 'var(--cb-border)', cursor: 'pointer' }}><X size={10} /></button>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add a task…"
                onKeyDown={e => e.key === 'Enter' && addTask()}
                style={{ flex: 1, background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11, color: 'var(--cb-text-sec)', outline: 'none' }} />
              <button onClick={addTask} style={{ padding: '4px 10px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>+</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scenes section ────────────────────────────────────────────────────────────
function CostumeScenesSection({ scenes, statusMap, cycleStatus, selectedScene, setSelectedScene, projectId, onDelete, fetchAll, token, onAddCostume }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const orderKey = `costume_scene_order_${projectId}`;
  const [sceneOrder, setSceneOrder] = useState(() => { try { return JSON.parse(localStorage.getItem(orderKey) || 'null'); } catch { return null; } });
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDrop = (targetId) => {
    if (!draggedId || draggedId === targetId) { setDraggedId(null); setDragOverId(null); return; }
    const base = sceneOrder || filtered.map(s => s.id);
    const from = base.indexOf(draggedId), to = base.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...base]; next.splice(from, 1); next.splice(to, 0, draggedId);
    setSceneOrder(next); localStorage.setItem(orderKey, JSON.stringify(next));
    setDraggedId(null); setDragOverId(null);
  };

  const uniqueLocs = [...new Set(scenes.map(s => s.location_name).filter(Boolean))];
  const filtered = scenes.filter(s => {
    if (statusFilter !== 'all' && (statusMap[s.id] || 'unreviewed') !== statusFilter) return false;
    if (locationFilter !== 'all' && s.location_name !== locationFilter) return false;
    if (search) {
      const q = search.toLowerCase().trim();
      const sceneNumStr = formatSceneNumber(s.scene_number).toLowerCase();
      return sceneNumStr.includes(q) ||
             s.scene_header?.toLowerCase().includes(q) || s.location_name?.toLowerCase().includes(q) ||
             (s.characters_in_scene||[]).some(c => c.toLowerCase().includes(q));
    }
    return true;
  });

  const orderedScenes = sceneOrder
    ? [...filtered].sort((a, b) => { const ai = sceneOrder.indexOf(a.id), bi = sceneOrder.indexOf(b.id); if (ai === -1 && bi === -1) return 0; if (ai === -1) return 1; if (bi === -1) return -1; return ai - bi; })
    : filtered;

  const cols = selectedScene ? 2 : (viewMode === 'grid' ? 3 : 1);

  const selectedIdx = orderedScenes.findIndex(s => s.id === selectedScene?.id);
  const navigateScene = (dir) => {
    const next = orderedScenes[selectedIdx + dir];
    if (next) setSelectedScene(next);
  };

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--cb-border)', borderRadius: 6, padding: '4px 10px', background: 'var(--cb-surface)', minWidth: 160 }}>
            <Search size={11} style={{ color: 'var(--cb-text-dim)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search scenes..."
              style={{ background: 'none', border: 'none', outline: 'none', fontSize: 11, color: 'var(--cb-text-sec)', width: '100%' }} />
          </div>
          <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)' }}>
            <option value="all">All Locations</option>
            {uniqueLocs.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)' }}>
            <option value="all">All Status</option>
            {Object.entries(COSTUME_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {(search || statusFilter !== 'all' || locationFilter !== 'all') && (
            <button onClick={() => { setSearch(''); setStatusFilter('all'); setLocationFilter('all'); }} style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-accent)', background: 'transparent', cursor: 'pointer' }}>Clear</button>
          )}
          <div style={{ display: 'flex', border: '1px solid var(--cb-border)', borderRadius: 6, overflow: 'hidden', marginLeft: 'auto' }}>
            {[['grid', <LayoutGrid size={11} />], ['list', <List size={11} />]].map(([m, icon]) => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding: '5px 10px', background: viewMode === m ? 'var(--cb-border)' : 'transparent', color: viewMode === m ? 'var(--cb-accent)' : 'var(--cb-text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', borderRight: m === 'grid' ? '1px solid var(--cb-border)' : 'none' }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, transition: 'grid-template-columns .2s' }}>
            {orderedScenes.map(scene => (
              <CostumeSceneCard key={scene.id} scene={scene} status={statusMap[scene.id] || 'unreviewed'}
                onSelect={s => setSelectedScene(prev => prev?.id === s.id ? null : s)}
                onCycleStatus={cycleStatus} onAddCostume={() => onAddCostume(scene)}
                isSelected={selectedScene?.id === scene.id}
                isDragOver={dragOverId === scene.id}
                onDragStart={() => setDraggedId(scene.id)}
                onDragOver={() => setDragOverId(scene.id)}
                onDrop={() => handleDrop(scene.id)}
                onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
              />
            ))}
            {orderedScenes.length === 0 && (
              <div style={{ gridColumn: `span ${cols}`, textAlign: 'center', padding: 48, color: 'var(--cb-border)', fontSize: 12 }}>No scenes found</div>
            )}
          </div>

          {/* Add New Scene */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <button onClick={() => toast.info('Add scene from the Script Upload page')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', border: '1px dashed var(--cb-border)', borderRadius: 8, fontSize: 12, color: 'var(--cb-text-dim)', background: 'transparent', cursor: 'pointer' }}
              data-testid="add-new-scene-btn">
              <Plus size={14} style={{ color: 'var(--cb-text-dim)' }} /> Add New Scene
            </button>
          </div>
        </div>
      </div>

      {/* Right panel */}
      {selectedScene && (
        <CostumeRightPanel scene={selectedScene} status={statusMap[selectedScene.id] || 'unreviewed'}
          onClose={() => setSelectedScene(null)} onCycleStatus={cycleStatus}
          onDelete={onDelete} fetchAll={fetchAll} token={token}
          allScenes={orderedScenes} sceneIndex={selectedIdx}
          onNavigate={dir => navigateScene(dir)}
          onAddCostume={(scene) => onAddCostume(scene)} />
      )}
    </div>
  );
}

// ── Placeholder section ───────────────────────────────────────────────────────
function PlaceholderSection({ label }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--cb-border)' }}>
      <Shirt size={32} style={{ marginBottom: 12, opacity: .3 }} />
      <p style={{ fontSize: 14, fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>Coming soon</p>
    </div>
  );
}

// ── Characters section — links to Casting dept (same data) ───────────────────
function CharactersSection({ scenes, projectId, navigate }) {
  const charMap = {};
  scenes.forEach(s => {
    (s.characters_in_scene || []).forEach(c => {
      if (!charMap[c]) charMap[c] = { name: c, scenes: [], looks: [] };
      charMap[c].scenes.push(s);
      charMap[c].looks.push(...(s.breakdown_items?.COSTUME || []).filter(i => i.character_name === c));
    });
  });
  const chars = Object.values(charMap).sort((a, b) => b.scenes.length - a.scenes.length);

  const AVATAR_COLORS = ['#6366f1','#ec4899','#f97316','#14b8a6','#a855f7','#0ea5e9','#84cc16','#f59e0b'];
  const avatarColor = (name) => AVATAR_COLORS[(name||'').split('').reduce((a,c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      {/* Banner linking to Casting */}
      <div style={{ background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, color: 'var(--cb-accent)' }}>
          Characters here are the same as Casting department. Full profiles, photos and shoot days are managed there.
        </div>
        <button onClick={() => navigate(`/projects/${projectId}/dept/casting`)}
          style={{ padding: '5px 14px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 12 }}>
          Open Casting →
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {chars.map(char => (
          <div key={char.name}
            onClick={() => navigate(`/projects/${projectId}/dept/casting`)}
            style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, padding: '16px 14px', textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(245,166,35,.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--cb-border)'}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: avatarColor(char.name), margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--cb-text)' }}>
              {char.name.slice(0,2).toUpperCase()}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cb-text)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{char.name}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cb-accent)' }}>{char.scenes.length}</div>
                <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>Scenes</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>{char.looks.length}</div>
                <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>Looks</div>
              </div>
            </div>
          </div>
        ))}
        {chars.length === 0 && <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: 48, color: 'var(--cb-border)', fontSize: 12 }}>No characters found</div>}
      </div>
    </div>
  );
}
// ── Main Component ────────────────────────────────────────────────────────────
export default function CostumeDepartmentPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [section, setSection] = useState('scenes');
  const [scenes, setScenes] = useState([]);
  const [allScenes, setAllScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScene, setSelectedScene] = useState(null);

  const [costumeStatusMap, setCostumeStatusMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`costume_status_${projectId}`) || '{}'); } catch { return {}; }
  });

  const [showDialog, setShowDialog] = useState(false);
  const [dialogSceneId, setDialogSceneId] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfLanguage, setPdfLanguage] = useState('english');
  const [showLangDialog, setShowLangDialog] = useState(false);
  const [projectLang, setProjectLang] = useState('english');
  const [showAIModal, setShowAIModal] = useState(false);
  const [newCostume, setNewCostume] = useState({ ...BLANK_COSTUME });
  const [saving, setSaving] = useState(false);
  const [recycled, setRecycled] = useState([]);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  // Quick entry
  const [quickText, setQuickText] = useState('');
  const [quickScenes, setQuickScenes] = useState('');
  const [quickCharacter, setQuickCharacter] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/breakdown`, { headers });
      const sceneData = res.data;
      setScenes(sceneData);
      setAllScenes(sceneData);
      const rec = [];
      sceneData.forEach(s => (s.breakdown_items?.COSTUME || []).filter(i => i.is_flagged).forEach(i => rec.push({ ...i, scene: s })));
      setRecycled(rec);
    } catch { toast.error('Failed to load costume data'); }
    finally { setLoading(false); }
  }, [projectId, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch project language once (mirrors ArtDepartmentPage pattern)
  useEffect(() => {
    if (!projectId || !token) return;
    axios.get(`${API}/projects/${projectId}`, { headers })
      .then(r => {
        const p = r.data || {};
        axios.get(`${API}/projects/${projectId}/scripts`, { headers })
          .then(sr => {
            const scripts = sr.data || [];
            const active = scripts.find(s => !s.is_archived) || scripts[0];
            const lang = active?.breakdown_language || p?.demo_key || 'english';
            setProjectLang(lang);
          })
          .catch(() => setProjectLang(p?.demo_key || 'english'));
      })
      .catch(() => {});
  }, [projectId, token, headers]);

  const isEnglishProj = !projectLang || projectLang === 'english' || projectLang === 'en';

  const handleExportPdf = useCallback(() => {
    if (isEnglishProj) {
      setPdfLanguage('english');
      setShowPdfPreview(true);
    } else {
      setShowLangDialog(true);
    }
  }, [isEnglishProj]);

  const handleLangSelect = useCallback((lang) => {
    setShowLangDialog(false);
    setPdfLanguage(lang);
    setShowPdfPreview(true);
  }, []);

  const cycleStatus = (sceneId) => {
    const cur = costumeStatusMap[sceneId] || 'unreviewed';
    const idx = STATUS_CYCLE.indexOf(cur);
    const next = { ...costumeStatusMap, [sceneId]: STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length] };
    setCostumeStatusMap(next);
    try { localStorage.setItem(`costume_status_${projectId}`, JSON.stringify(next)); } catch {}
  };

  const handleAddCostume = async () => {
    if (!newCostume.description.trim()) { toast.error('Costume name required'); return; }
    const sceneIds = newCostume.sceneIds.length > 0 ? newCostume.sceneIds : (dialogSceneId ? [dialogSceneId] : []);
    if (!sceneIds.length) { toast.error('Select at least one scene'); return; }
    setSaving(true);
    try {
      await Promise.all(sceneIds.map(sid =>
        axios.post(`${API}/projects/${projectId}/breakdown-items`, { scene_id: sid, category: 'COSTUME', description: newCostume.description, character_name: newCostume.character_name, asset_sub_type: newCostume.asset_sub_type, source: newCostume.source, notes: newCostume.notes }, { headers })
      ));
      toast.success('Costume added');
      setShowDialog(false); setNewCostume({ ...BLANK_COSTUME }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleQuickAdd = async () => {
    const lines = quickText.split('\n').map(l => l.trim()).filter(Boolean);
    const sceneIdList = quickScenes.split(',').map(s => s.trim()).filter(Boolean)
      .map(num => allScenes.find(s => String(s.scene_number) === num)?.id).filter(Boolean);
    if (!lines.length) { toast.error('Enter costume names'); return; }
    if (!sceneIdList.length) { toast.error('Enter valid scene numbers'); return; }
    setQuickAdding(true);
    try {
      const ops = [];
      for (const sceneId of sceneIdList)
        for (const name of lines)
          ops.push(axios.post(`${API}/projects/${projectId}/breakdown-items`, { scene_id: sceneId, category: 'COSTUME', description: name, character_name: quickCharacter }, { headers }));
      await Promise.all(ops);
      toast.success(`Added ${lines.length} costume(s)`);
      setQuickText(''); setQuickScenes(''); setQuickCharacter(''); fetchAll();
    } catch { toast.error('Failed'); }
    finally { setQuickAdding(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await axios.delete(`${API}/breakdown-items/${itemId}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleFlagItem = async (itemId, flagged) => {
    try { await axios.patch(`${API}/breakdown-items/${itemId}`, { is_flagged: !flagged }, { headers }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const openAddFor = (scene) => {
    setDialogSceneId(scene?.id || null);
    setNewCostume({ ...BLANK_COSTUME, sceneIds: scene ? [scene.id] : [] });
    setShowDialog(true);
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)', overflow: 'hidden' }}>
      <CostumeSidebar section={section} setSection={setSection} onAddCostume={() => openAddFor(null)} onAddCharacter={() => navigate(`/projects/${projectId}/dept/casting`)} onAISuggest={() => setShowAIModal(true)} onRecycleBin={() => setShowRecycleBin(true)} user={user} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <CostumeHeader scenes={scenes} section={section} setSection={setSection} onAddCostume={() => openAddFor(null)} onExport={() => toast.info('Export coming soon')} onExportPdf={handleExportPdf} />

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cb-text-dim)', fontSize: 13 }}>Loading costume data…</div>
        ) : (
          <>
            {section === 'scenes' && (
              <CostumeScenesSection scenes={scenes} statusMap={costumeStatusMap} cycleStatus={cycleStatus} selectedScene={selectedScene} setSelectedScene={setSelectedScene} projectId={projectId} onDelete={handleDeleteItem} fetchAll={fetchAll} token={token} onAddCostume={openAddFor} />
            )}
            {section === 'characters' && <CharactersSection scenes={scenes} projectId={projectId} navigate={navigate} />}
            {section === 'looks' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {scenes.flatMap(s => (s.breakdown_items?.COSTUME || []).map(item => ({ ...item, scene: s }))).map((item, idx) => (
                    <div key={`${item.id}-${idx}`} style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ height: 100, background: 'var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {item.image_paths?.length > 0 ? <AuthImage path={item.image_paths[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 28 }}>👔</span>}
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--cb-text)', marginBottom: 2 }}>{item.description}</div>
                        {item.character_name && <div style={{ fontSize: 9, color: avatarColor(item.character_name) }}>{item.character_name}</div>}
                        <div style={{ fontSize: 9, color: 'var(--cb-text-dim)', marginTop: 2 }}>SC.{item.scene?.scene_number}</div>
                      </div>
                    </div>
                  ))}
                  {scenes.flatMap(s => s.breakdown_items?.COSTUME || []).length === 0 && <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: 48, color: 'var(--cb-border)' }}>No looks recorded</div>}
                </div>
              </div>
            )}
            {section === 'itembox' && (
              <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                {/* Quick Entry */}
                <div style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, padding: 20, marginBottom: 20 }} data-testid="costume-quick-entry">
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cb-text)', marginBottom: 4 }}>Quick Costume Entry</div>
                  <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 12 }}>Enter costume items (one per line), character, and scene numbers.</p>
                  <Textarea value={quickText} onChange={e => setQuickText(e.target.value)} placeholder="Blue Suit&#10;Red Dress&#10;Military Jacket"
                    className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[80px] text-sm mb-3" data-testid="costume-quick-textarea" />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 4, display: 'block' }}>Character Name</label>
                      <Input value={quickCharacter} onChange={e => setQuickCharacter(e.target.value)} placeholder="e.g. Sarah" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 4, display: 'block' }}>Scene Numbers (comma-separated)</label>
                      <Input value={quickScenes} onChange={e => setQuickScenes(e.target.value)} placeholder="e.g. 1,2,3" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={handleQuickAdd} disabled={quickAdding} className="bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold" data-testid="costume-quick-add-btn">
                      <Plus className="h-4 w-4 mr-1" />{quickAdding ? 'Adding...' : 'Add Costumes'}
                    </Button>
                  </div>
                </div>
                {/* Gallery */}
                <LookAndFeelGallery projectId={projectId} department={DEPT} />
              </div>
            )}
            {section === 'tasks' && (
              <div data-testid="costume-tasks-section" style={{ flex: 1, padding: 20, overflow: 'auto' }}>
                <DepartmentTasksPanel projectId={projectId} department="COSTUME" />
              </div>
            )}
            {!['scenes', 'characters', 'looks', 'itembox', 'tasks'].includes(section) && (
              <PlaceholderSection label={{ fittings: 'Fittings', reports: 'Reports' }[section] || section} />
            )}
          </>
        )}
      </div>

      <PDFPreviewDialog
        open={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        pdfUrl={`${API}/projects/${projectId}/costume/export-pdf?language=${pdfLanguage}`}
        fetchHeaders={{ Authorization: `Bearer ${token}` }}
        title="Costume Department Breakdown — Preview"
        downloadName={`Costume_Department_${projectId}_${pdfLanguage}.pdf`}
      />
      <LanguageDownloadDialog
        open={showLangDialog}
        projectLanguage={projectLang}
        onClose={() => setShowLangDialog(false)}
        onSelect={handleLangSelect}
      />
      <ArtAIGenerateModal
        open={showAIModal}
        onClose={() => setShowAIModal(false)}
        projectId={projectId}
        sceneId={null}
        allScenes={scenes}
        token={token}
        onAssetAdded={() => fetchAll()}
        department="COSTUME"
      />

      {/* ── Add Costume Dialog ── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-2xl">
          <DialogHeader><DialogTitle className="text-xl font-semibold">New Costume Item</DialogTitle><DialogDescription className="sr-only">Add a new costume item</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Costume Name *</label>
                <Input value={newCostume.description} onChange={e => setNewCostume(p => ({ ...p, description: e.target.value }))} className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="costume-name-input" />
              </div>
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Character</label>
                <Input value={newCostume.character_name} onChange={e => setNewCostume(p => ({ ...p, character_name: e.target.value }))} placeholder="Character name" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Category</label>
                <Select value={newCostume.asset_sub_type} onValueChange={v => setNewCostume(p => ({ ...p, asset_sub_type: v }))}>
                  <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                    {COSTUME_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-[var(--cb-text)]">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Source</label>
                <Select value={newCostume.source} onValueChange={v => setNewCostume(p => ({ ...p, source: v }))}>
                  <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                    {SOURCES.map(s => <SelectItem key={s} value={s} className="text-[var(--cb-text)] capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Notes</label>
              <Textarea value={newCostume.notes} onChange={e => setNewCostume(p => ({ ...p, notes: e.target.value }))} className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-2 block">Used in Scenes</label>
              <div className="flex flex-wrap gap-2">
                {allScenes.map(scene => (
                  <button key={scene.id}
                    onClick={() => setNewCostume(p => ({ ...p, sceneIds: p.sceneIds.includes(scene.id) ? p.sceneIds.filter(s => s !== scene.id) : [...p.sceneIds, scene.id] }))}
                    className="h-9 w-9 rounded text-sm font-medium transition-colors"
                    style={{ background: newCostume.sceneIds.includes(scene.id) ? 'var(--cb-accent)' : 'var(--cb-border)', color: newCostume.sceneIds.includes(scene.id) ? 'var(--cb-bg)' : 'var(--cb-text-sec)' }}
                    data-testid={`costume-scene-${scene.scene_number ?? 'unnumbered'}`}>
                    {formatSceneNumber(scene.scene_number)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAddCostume} disabled={saving} className="bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold" data-testid="create-costume-btn">
                {saving ? 'Adding…' : 'Add Costume'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Recycle Bin ── */}
      <Dialog open={showRecycleBin} onOpenChange={setShowRecycleBin}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader><DialogTitle className="text-xl font-semibold">Recycle Bin</DialogTitle></DialogHeader>
          {recycled.length === 0
            ? <p className="text-[var(--cb-text-sec)] text-sm py-4 text-center">Recycle bin is empty</p>
            : <div className="space-y-2 max-h-80 overflow-y-auto">
              {recycled.map(item => (
                <div key={item.id} className="flex items-center justify-between bg-[var(--cb-bg)] p-3 rounded">
                  <div>
                    <div className="text-sm text-[var(--cb-text)]">{item.description}</div>
                    <div className="text-xs text-[var(--cb-text-sec)]">Scene {item.scene?.scene_number}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleFlagItem(item.id, true)} className="text-green-400 text-xs h-7">Restore</Button>
                </div>
              ))}
            </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
